import { ContributionVEFStatus, } from '../types';
/**
 * DemandesFinancement formatter
 */
class ContributionsVEFFormatter {
    i18nService;
    dateFormatterService;
    constructor({ i18nService, dateFormatterService }) {
        this.i18nService = i18nService;
        this.dateFormatterService = dateFormatterService;
    }
    /**
     * Build title for contribution VEF
     */
    buildFormattedContributionVEFTitle(demandeApprenant) {
        let title = '';
        const events = demandeApprenant.contributionVef.history?.events ?? [];
        const requestedEvent = events.find((event) => event?.reference === ContributionVEFStatus.REQUESTED);
        const checkedEvent = events.find((event) => event?.reference === ContributionVEFStatus.CHECKED);
        if (requestedEvent || checkedEvent) {
            title = this.i18nService.translate('contributions-vef.title', {
                discipline: demandeApprenant.bourse.formationTitle ?? '',
                anneeFormation: demandeApprenant.bourse.formationAnnee ? `- ${demandeApprenant.bourse.formationAnnee}` : '',
                beneficiaireFullName: demandeApprenant.beneficiaires[0]?.fullName ?? '',
                referenceAdministrative: demandeApprenant.referenceAdministrative ?? '',
            });
        }
        return title;
    }
    /**
     * Build bourse info for contribution VEF
     */
    async buildFormattedContributionVEFBourseInfo(demandeApprenant) {
        let dateEntree = '';
        if (demandeApprenant.bourse.formationDateEntree) {
            dateEntree = this.dateFormatterService.asDate(demandeApprenant.bourse.formationDateEntree, { style: 'short' });
        }
        let dateFin = '';
        if (demandeApprenant.bourse.formationDateFin) {
            dateFin = this.dateFormatterService.asDate(demandeApprenant.bourse.formationDateFin, { style: 'short' });
        }
        return this.i18nService.translate('contributions-vef.bourse-info', {
            dateEntree,
            dateFin,
            cursus: demandeApprenant.bourse.formationCursus ? `- ${demandeApprenant.bourse.formationCursus}` : '',
            nombreHeuresFormation: demandeApprenant.bourse.formationNombreHeures
                ? `- ${demandeApprenant.bourse.formationNombreHeures}h`
                : '',
        });
    }
    /**
     * Build actions for contribution VEF
     */
    buildFormattedContributionVEFActions(demandeApprenant) {
        const formattedActions = demandeApprenant.primaryActions
            .map((action) => this.formatContributionVEFAction(action))
            .filter((action) => action !== undefined);
        return formattedActions ?? [];
    }
    /**
     * format a contribution VEF to be used by a front component
     */
    async formatContributionVEF(demandeApprenant) {
        const result = {
            referenceDemande: demandeApprenant.reference,
            referenceContributionVEF: demandeApprenant.contributionVef.reference,
            title: this.buildFormattedContributionVEFTitle(demandeApprenant),
            bourseInfo: await this.buildFormattedContributionVEFBourseInfo(demandeApprenant),
            actions: this.buildFormattedContributionVEFActions(demandeApprenant),
            status: demandeApprenant.contributionVef.status,
            beneficiaireFullName: demandeApprenant.beneficiaires[0]?.fullName ?? '',
        };
        if (demandeApprenant.contributionVef.commentaire) {
            result.commentaire = demandeApprenant.contributionVef.commentaire;
        }
        if (demandeApprenant.bourse.contributionVEFStatus) {
            result.tag = this.formatContributionVEFTag(demandeApprenant.bourse.contributionVEFStatus);
        }
        return result;
    }
    /**
     * Format contribution VEF tag for front
     * @param status - contribution VEF status
     * @returns a formatted tag
     */
    formatContributionVEFTag(status) {
        let formattedTag;
        switch (status) {
            case ContributionVEFStatus.REQUESTED: {
                formattedTag = {
                    color: 'warning',
                    title: this.i18nService.translate('contributions-vef.requested'),
                };
                break;
            }
            case ContributionVEFStatus.CHECKED: {
                formattedTag = {
                    color: 'success',
                    title: this.i18nService.translate('contributions-vef.checked'),
                    icon: 'check',
                };
                break;
            }
        }
        return formattedTag;
    }
    /**
     * Format action to display button
     * @param action - action that will correspond to a button
     * @returns a formatted action
     */
    formatContributionVEFAction(action) {
        let formattedAction;
        switch (action.type) {
            case 'check-validate': {
                formattedAction = {
                    id: 'check-validate',
                    title: this.i18nService.translate('contributions-vef.check-and-validate'),
                    icon: { icon: 'chevron-right' },
                    variant: 'primary',
                };
                break;
            }
            case 'validate': {
                formattedAction = {
                    id: 'validate',
                    title: this.i18nService.translate('contributions-vef.validate'),
                    icon: { icon: 'chevron-right' },
                    variant: 'primary',
                };
                break;
            }
            case 'fast-validate': {
                formattedAction = {
                    id: 'fast-validate',
                    title: this.i18nService.translate('contributions-vef.fast-validate'),
                    icon: { icon: 'check' },
                    variant: 'secondary',
                };
                break;
            }
            case 'check': {
                formattedAction = {
                    id: 'check',
                    title: this.i18nService.translate('contributions-vef.check'),
                    icon: { icon: 'chevron-right' },
                    variant: 'primary',
                };
                break;
            }
        }
        return formattedAction;
    }
    /**
     * format a contribution VEF list to be used by a front component
     */
    async formatContributionsVEFList(demandesApprenants) {
        return await Promise.all(demandesApprenants.map((demandeApprenant) => {
            return this.formatContributionVEF(demandeApprenant);
        }));
    }
}
export { ContributionsVEFFormatter };
//# sourceMappingURL=contributions-vef-formatter.js.map